import { useState, useEffect, useRef, useCallback } from 'react';
import { NumericFormat } from 'react-number-format';
import { formatPercentage } from './utils/formatUtils';
import { getCategoryName } from './utils/categoryUtils';
import { getBusinessDays } from './utils/dateUtils';
import { ChevronRight, Plus, X } from 'lucide-react';
import projectService from "../../store/services/projects.service";
import { debounce } from 'lodash';
import { useDispatch } from "react-redux";
import { generalActions } from '../../store/actions';
import { useColumnWidths } from './useColumnWidths';
import HoverCard from '../../components/hoverCard/HoverCard';
import { Avatar } from '@fluentui/react-components';

const CashFlowRow = ({
  projectId,
  columns,
  category,
  cashFlowData,
  onInAmountChange,
  onOutAmountChange,
  onForecastChange,
  onDescriptionChange,
  onAddNewLine,
  onRemoveLine,
  settings = { currencySymbol: '$', groupSeparator: ',', decimalSeparator: '.' },
  onForecastPairChange,
  onActualHoursChange,
  isReadOnly = true,
}) => {
  // Called here so columnWidths state drives re-renders of this component
  const { columnWidths, getWidth } = useColumnWidths();

  // w() helper — depends on columnWidths so cells re-render on resize
  const w = (colName) => ({ width: columnWidths[colName] ?? getWidth(colName), minWidth: columnWidths[colName] ?? getWidth(colName), flexShrink: 0 });

  const [expanded, setExpanded] = useState(false);
  const [expandedParents, setExpandedParents] = useState(new Set());
  const [descriptions, setDescriptions] = useState({});

  const toggleParent = (lineId) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };
  const [updateCheckLoading, setUpdateCheckLoading] = useState(false);
  const [canUpdateLine, setCanUpdateLine] = useState(null);
  const descriptionDebounced = useRef(debounce((line, value) => onDescriptionChange(line, value), 1000));
  const dispatch = useDispatch();

  const getCol = (name) => columns.find(c => c.name === name);
  const descriptionCol = getCol("Description");
  const forecastAllocationCol = getCol("For. Allocation");
  const forecastHoursCol = getCol("For. Hours");
  const actualHoursCol = getCol("Act. Hours");
  const ForecastHvCol = getCol("For. HV");
  const forecastHCostCol = getCol("For. h. Cost");
  const forecastHBillCol = getCol("For. h. Bill");
  const forecastTotalCostCol = getCol("For. Total Cost");
  const forecastTotalBillCol = getCol("For. Total Bill");
  const forecastCvCol = getCol("For. CV");
  const forecastGpCol = getCol("For. GP");
  const invCol = getCol("INV");
  const acCol = getCol("AC");
  const gpCol = getCol("GP");
  const totalsCol = getCol("Totals");

  useEffect(() => {
    const initialDescriptions = {};
    cashFlowData.forEach(monthData => {
      monthData.lines.forEach(line => {
        if (line.category === category) {
          initialDescriptions[line.id] = line.description || '';
        }
      });
    });
    setDescriptions(initialDescriptions);
  }, [cashFlowData, category]);

  // Returns top-level lines for this category, each with a `children` array of sub-lines.
  const getLinesForCategory = () => {
    const linesMap = new Map();
    cashFlowData.forEach(monthData => {
      monthData.lines.forEach(line => {
        if (line.category !== category) return;
        if (!linesMap.has(line.id)) {
          linesMap.set(line.id, {
            id: line.id,
            description: line.description,
            category: line.category,
            parentLineId: line.parentLineId || null,
            linkedResource: line.linkedResource || null,
            months: new Map()
          });
        }
        linesMap.get(line.id).months.set(monthData.month, {
          inv: line.inv,
          ac: line.ac,
          gp: line.gp,
          gpPercentage: line.gpPercentage,
          itemId: line.itemId,
          isNew: line.isNew,
          isLocked: line.isLocked,
          forecastAllocation: line.forecastAllocation || 0,
          forecastHours: line.forecastHours || 0,
          forecastHourlyCostRate: line.forecastHourlyCostRate || 0,
          forecastHourlyBillRate: line.forecastHourlyBillRate || 0,
          forecastTotalCost: line.forecastTotalCost || 0,
          forecastTotalBill: line.forecastTotalBill || 0,
          cv: line.cv || 0,
          actualHours: line.actualHours || 0,
          ForecastHv: line.ForecastHv || 0,
        });
      });
    });

    // Separate top-level lines from sub-lines, attach sub-lines to their parent
    const topLevel = [];
    linesMap.forEach(line => {
      if (!line.parentLineId) {
        line.children = [];
        topLevel.push(line);
      }
    });
    linesMap.forEach(line => {
      if (line.parentLineId && linesMap.has(line.parentLineId)) {
        linesMap.get(line.parentLineId).children = linesMap.get(line.parentLineId).children || [];
        linesMap.get(line.parentLineId).children.push(line);
      }
    });

    // Sort top-level lines alphabetically by description
    topLevel.sort((a, b) => (a.description || '').localeCompare(b.description || ''));
    // Sort each parent's children by resource name (or description as fallback)
    topLevel.forEach(line => {
      if (line.children?.length > 1) {
        line.children.sort((a, b) => {
          const nameA = a.linkedResource?.name || a.description || '';
          const nameB = b.linkedResource?.name || b.description || '';
          return nameA.localeCompare(nameB);
        });
      }
    });

    return topLevel;
  };

  const calculateTotals = () => {
    let totalInv = 0, totalAc = 0;
    cashFlowData.forEach(monthData => {
      monthData.lines.forEach(line => {
        // Exclude sub-lines from category totals (parent already aggregates them)
        if (line.category === category && !line.parentLineId) {
          totalInv += line.inv || 0;
          totalAc += line.ac || 0;
        }
      });
    });
    const totalGp = totalInv - totalAc;
    const totalGpPercentage = totalInv !== 0 ? (totalGp / totalInv) * 100 : 0;
    return { totalInv, totalAc, totalGp, totalGpPercentage };
  };

  const calculateCategoryForecastTotals = () => {
    const categoryLines = getLinesForCategory();
    // Only sum top-level lines (children have already been aggregated into their parent)
    const totals = categoryLines.reduce((acc, line) => {
      const lineTotals = calculateLineForecastTotals(line.months);
      return {
        totalCv: acc.totalCv + lineTotals.totalCv,
        totalForecastHours: acc.totalForecastHours + lineTotals.totalForecastHours,
        totalForecastTotalCost: acc.totalForecastTotalCost + lineTotals.totalForecastTotalCost,
        totalForecastTotalBill: acc.totalForecastTotalBill + lineTotals.totalForecastTotalBill,
        avgForecastAllocation: acc.avgForecastAllocation + lineTotals.avgForecastAllocation,
        totalActualHours: acc.totalActualHours + lineTotals.totalActualHours,
        totalForecastHv: acc.totalForecastHv + lineTotals.totalForecastHv,
      };
    }, {
      totalCv: 0, totalForecastHours: 0, totalForecastTotalCost: 0, totalForecastTotalBill: 0,
      avgForecastAllocation: 0, totalActualHours: 0, totalForecastHv: 0,
    });
    return {
      ...totals,
      avgForecastHourlyCostRate: totals.totalForecastHours > 0 ? totals.totalForecastTotalCost / totals.totalForecastHours : 0,
      avgForecastHourlyBillRate: totals.totalForecastHours > 0 ? totals.totalForecastTotalBill / totals.totalForecastHours : 0,
    };
  };

  const calculateLineForecastTotals = (lineMonths) => {
    const monthValues = Array.from(lineMonths.values());
    const nonZeroMonths = monthValues.filter(m => (m.forecastAllocation || 0) !== 0);
    const totalForecastHours = monthValues.reduce((s, m) => s + (m.forecastHours || 0), 0);
    const totalForecastTotalCost = monthValues.reduce((s, m) => s + (m.forecastTotalCost || 0), 0);
    const totalForecastTotalBill = monthValues.reduce((s, m) => s + (m.forecastTotalBill || 0), 0);
    return {
      totalCv: monthValues.reduce((s, m) => s + (m.cv || 0), 0),
      totalForecastHours,
      totalForecastTotalCost,
      totalForecastTotalBill,
      avgForecastAllocation: nonZeroMonths.length > 0 ? nonZeroMonths.reduce((s, m) => s + m.forecastAllocation, 0) / nonZeroMonths.length : 0,
      avgForecastHourlyCostRate: totalForecastHours > 0 ? totalForecastTotalCost / totalForecastHours : 0,
      avgForecastHourlyBillRate: totalForecastHours > 0 ? totalForecastTotalBill / totalForecastHours : 0,
      totalActualHours: monthValues.reduce((s, m) => s + (m.actualHours || 0), 0),
      totalForecastHv: monthValues.reduce((s, m) => s + (m.ForecastHv || 0), 0),
    };
  };

  const handleAddRow = () => { onAddNewLine(category); setExpanded(true); };
  const handleRemoveRow = (lineId) => { onRemoveLine(lineId); };

  const descriptionHandler = (line) => {
    setUpdateCheckLoading(true);
    const data = { projectId, lineId: line.id };
    projectService.canUpdateProjectCashflowLineRequest(data)
      .then((data) => {
        if (data) {
          dispatch(generalActions.showToast({ title: data?.data?.message || "Something went wrong.", type: "error" }));
          setCanUpdateLine(false);
        } else { setCanUpdateLine(true); }
      })
      .catch((error) => {
        setCanUpdateLine(false);
        dispatch(generalActions.showToast({ title: error?.data?.message || "Something went wrong.", type: "error" }));
      })
      .finally(() => setUpdateCheckLoading(false));
  };

  const checkCanUpdateDesc = (line, value) => {
    if (canUpdateLine && canUpdateLine === true) handleDescriptionChange(line, value);
  };

  const handleDescriptionChange = useCallback((line, value) => {
    setDescriptions(prev => ({ ...prev, [line.id]: value }));
    descriptionDebounced.current(line, value);
  }, []);

  const { totalInv, totalAc, totalGp, totalGpPercentage } = calculateTotals();
  const categoryForecastTotals = calculateCategoryForecastTotals();
  const lines = getLinesForCategory();

  // Category header aggregates per month helper — exclude sub-lines to avoid double-counting
  const getCatMonthSum = (monthData, field) =>
    monthData.lines.filter(l => l.category === category && !l.parentLineId).reduce((s, l) => s + (l[field] || 0), 0);

  // Reusable totals cells renderer — same layout as a month column
  const renderTotalsCells = (forecastTotals, totalInvVal, totalAcVal, totalGpPctVal, isCvVisible) => {
    const forecastGpPct = forecastTotals.totalForecastTotalBill > 0 ? ((forecastTotals.totalForecastTotalBill - forecastTotals.totalForecastTotalCost) / forecastTotals.totalForecastTotalBill) * 100 : 0;
    return (
      <div className="in-out-inputs">
        {forecastAllocationCol?.isVisible && <NumericFormat style={w("For. Allocation")} value={forecastTotals.avgForecastAllocation} decimalScale={2} displayType="text" thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className="forecast-cell" />}
        {forecastHoursCol?.isVisible && <NumericFormat style={w("For. Hours")} value={forecastTotals.totalForecastHours} decimalScale={2} displayType="text" thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className="forecast-cell" />}
        {actualHoursCol?.isVisible && <NumericFormat style={w("Act. Hours")} value={forecastTotals.totalActualHours} decimalScale={2} displayType="text" thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className="forecast-cell" />}
        {ForecastHvCol?.isVisible && <NumericFormat style={w("For. HV")} value={forecastTotals.totalForecastHv} decimalScale={2} displayType="text" thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className={`forecast-cell cv-cell ${(forecastTotals.totalForecastHv < 0) && 'cv-slipping'}`} />}
        {forecastHCostCol?.isVisible && <NumericFormat style={w("For. h. Cost")} value={forecastTotals.avgForecastHourlyCostRate} decimalScale={2} displayType="text" prefix={settings.currencySymbol} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className="forecast-cell" />}
        {forecastHBillCol?.isVisible && <NumericFormat style={w("For. h. Bill")} value={forecastTotals.avgForecastHourlyBillRate} decimalScale={2} displayType="text" prefix={settings.currencySymbol} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className="forecast-cell" />}
        {forecastTotalCostCol?.isVisible && <NumericFormat style={w("For. Total Cost")} value={forecastTotals.totalForecastTotalCost} decimalScale={2} displayType="text" prefix={settings.currencySymbol} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className="forecast-cell" />}
        {forecastTotalBillCol?.isVisible && <NumericFormat style={w("For. Total Bill")} value={forecastTotals.totalForecastTotalBill} decimalScale={2} displayType="text" prefix={settings.currencySymbol} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className="forecast-cell" />}
        {invCol?.isVisible && <NumericFormat style={w("INV")} value={totalInvVal} decimalScale={2} displayType="text" prefix={settings.currencySymbol} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className="in-amount" />}
        {acCol?.isVisible && <NumericFormat style={w("AC")} value={totalAcVal} decimalScale={2} displayType="text" prefix={settings.currencySymbol} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className="out-amount" />}
        {forecastCvCol?.isVisible && <NumericFormat style={w("For. CV")} value={forecastTotals.totalCv} decimalScale={2} displayType="text" prefix={settings.currencySymbol} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className={`forecast-cell cv-cell ${(forecastTotals.totalCv < 0) && 'cv-slipping'}`} />}
        {forecastGpCol?.isVisible && <input style={w("For. GP")} type="text" className="gp-percentage" disabled value={formatPercentage(forecastGpPct)} />}
        {gpCol?.isVisible && <input style={w("GP")} type="text" className="gp-percentage" disabled value={formatPercentage(totalGpPctVal)} />}
      </div>
    );
  };

  return (
    <>
      <tr className={`category-group ${expanded ? 'expanded' : ''}`}>
        <td className={`category-column ${isReadOnly ? 'read-only' : ''}`}>
          <div className="category-header">
            <div className="category-name-section">
              <button className="expand-button" onClick={() => setExpanded(!expanded)}>
                <ChevronRight size={16} className={`transform ${expanded ? "rotate-90" : ""}`} />
                {getCategoryName(category)}
              </button>
              {!isReadOnly && (
                <button className="add-category-button" onClick={handleAddRow} title="Add new line">
                  <Plus size={16} />
                </button>
              )}
            </div>
          </div>
        </td>
        {descriptionCol?.isVisible !== false && <td className="description-column"></td>}

        {cashFlowData.map((monthData) => {
          const forecastAllocation = getCatMonthSum(monthData, 'forecastAllocation');
          const forecastHours = getCatMonthSum(monthData, 'forecastHours');
          const actualHours = getCatMonthSum(monthData, 'actualHours');
          const ForecastHv = getCatMonthSum(monthData, 'ForecastHv');
          const forecastTotalCost = getCatMonthSum(monthData, 'forecastTotalCost');
          const forecastTotalBill = getCatMonthSum(monthData, 'forecastTotalBill');
          const forecastHourlyCostRate = forecastHours > 0 ? forecastTotalCost / forecastHours : 0;
          const forecastHourlyBillRate = forecastHours > 0 ? forecastTotalBill / forecastHours : 0;
          const inv = getCatMonthSum(monthData, 'inv');
          const ac = getCatMonthSum(monthData, 'ac');
          const cv = getCatMonthSum(monthData, 'cv');

          const isCvSlipping = cv < 0;
          const gpPercentage = inv === 0 ? 0 : ((inv - ac) / inv) * 100;
          const forecastGpPercentage = forecastTotalBill > 0 ? ((forecastTotalBill - forecastTotalCost) / forecastTotalBill) * 100 : 0;

          return (
            <td
              key={`${monthData.year}-${monthData.month}`}
              className="month-column-content-container"
            >
              <div className="month-column-content">
                <div className="in-out-inputs">

                  {forecastAllocationCol?.isVisible && (
                    <NumericFormat
                      style={w("For. Allocation")}
                      value={forecastAllocation}
                      decimalScale={2}
                      displayType="text"
                      thousandSeparator={settings.groupSeparator}
                      decimalSeparator={settings.decimalSeparator}
                      className="forecast-cell"
                    />
                  )}

                  {forecastHoursCol?.isVisible && (
                    <NumericFormat
                      style={w("For. Hours")}
                      value={forecastHours}
                      decimalScale={2}
                      displayType="text"
                      thousandSeparator={settings.groupSeparator}
                      decimalSeparator={settings.decimalSeparator}
                      className="forecast-cell"
                    />
                  )}

                  {actualHoursCol?.isVisible && (
                    <NumericFormat
                      style={w("Act. Hours")}
                      value={actualHours}
                      decimalScale={2}
                      displayType="text"
                      thousandSeparator={settings.groupSeparator}
                      decimalSeparator={settings.decimalSeparator}
                      className="forecast-cell"
                    />
                  )}

                  {ForecastHvCol?.isVisible && (
                    <NumericFormat
                      style={w("For. HV")}
                      value={ForecastHv}
                      decimalScale={2}
                      displayType="text"
                      thousandSeparator={settings.groupSeparator}
                      decimalSeparator={settings.decimalSeparator}
                      className={`forecast-cell cv-cell ${ForecastHv < 0 && 'cv-slipping'}`}
                    />
                  )}

                  {forecastHCostCol?.isVisible && (
                    <NumericFormat
                      style={w("For. h. Cost")}
                      value={forecastHourlyCostRate}
                      decimalScale={2}
                      displayType="text"
                      prefix={settings.currencySymbol}
                      thousandSeparator={settings.groupSeparator}
                      decimalSeparator={settings.decimalSeparator}
                      className="forecast-cell"
                    />
                  )}

                  {forecastHBillCol?.isVisible && (
                    <NumericFormat
                      style={w("For. h. Bill")}
                      value={forecastHourlyBillRate}
                      decimalScale={2}
                      displayType="text"
                      prefix={settings.currencySymbol}
                      thousandSeparator={settings.groupSeparator}
                      decimalSeparator={settings.decimalSeparator}
                      className="forecast-cell"
                    />
                  )}

                  {forecastTotalCostCol?.isVisible && (
                    <NumericFormat
                      style={w("For. Total Cost")}
                      value={forecastTotalCost}
                      decimalScale={2}
                      displayType="text"
                      prefix={settings.currencySymbol}
                      thousandSeparator={settings.groupSeparator}
                      decimalSeparator={settings.decimalSeparator}
                      className="forecast-cell"
                    />
                  )}

                  {forecastTotalBillCol?.isVisible && (
                    <NumericFormat
                      style={w("For. Total Bill")}
                      value={forecastTotalBill}
                      decimalScale={2}
                      displayType="text"
                      prefix={settings.currencySymbol}
                      thousandSeparator={settings.groupSeparator}
                      decimalSeparator={settings.decimalSeparator}
                      className="forecast-cell"
                    />
                  )}

                  {invCol?.isVisible && (
                    <NumericFormat
                      style={w("INV")}
                      value={inv}
                      thousandSeparator={settings.groupSeparator}
                      decimalSeparator={settings.decimalSeparator}
                      prefix={settings.currencySymbol}
                      decimalScale={2}
                      disabled
                      className="in-amount"
                    />
                  )}

                  {acCol?.isVisible && (
                    <NumericFormat
                      style={w("AC")}
                      value={ac}
                      thousandSeparator={settings.groupSeparator}
                      decimalSeparator={settings.decimalSeparator}
                      prefix={settings.currencySymbol}
                      decimalScale={2}
                      disabled
                      className="out-amount"
                    />
                  )}

                  {forecastCvCol?.isVisible && (
                    <NumericFormat
                      style={w("For. CV")}
                      value={cv}
                      decimalScale={2}
                      displayType="text"
                      prefix={settings.currencySymbol}
                      thousandSeparator={settings.groupSeparator}
                      decimalSeparator={settings.decimalSeparator}
                      className={`forecast-cell cv-cell ${isCvSlipping && 'cv-slipping'}`}
                    />
                  )}

                  {forecastGpCol?.isVisible && (
                    <input
                      style={w("For. GP")}
                      type="text"
                      className="gp-percentage"
                      disabled
                      value={formatPercentage(forecastGpPercentage)}
                    />
                  )}

                  {gpCol?.isVisible && (
                    <input
                      style={w("GP")}
                      type="text"
                      className="gp-percentage"
                      disabled
                      value={formatPercentage(gpPercentage)}
                    />
                  )}

                </div>
              </div>
            </td>
          );
        })}

        {totalsCol?.isVisible !== false && (
          <td className="totals-column">
            <div className="month-column-content">
              {renderTotalsCells(
                categoryForecastTotals,
                totalInv,
                totalAc,
                totalGpPercentage,
              )}
            </div>
          </td>
        )}
      </tr>

      {expanded && lines.map(line => {
        const isParentLine = line.children && line.children.length > 0;
        const lineForecast = calculateLineForecastTotals(line.months);
        const lineInv = Array.from(line.months.values()).reduce((s, m) => s + (m.inv || 0), 0);
        const lineAc = Array.from(line.months.values()).reduce((s, m) => s + (m.ac || 0), 0);
        const lineGpPct = lineInv > 0 ? ((lineInv - lineAc) / lineInv) * 100 : 0;

        // Render individual line cells (shared by standalone and sub-line rows)
        const renderLineCells = (lineData, monthData, isSubLine = false) => {
          const mv = lineData.months.get(monthData.month) || { inv: 0, ac: 0, gp: 0, gpPercentage: 0, itemId: null, isNew: true, isLocked: false, forecastAllocation: 0, forecastHours: 0, forecastHourlyCostRate: 0, forecastHourlyBillRate: 0, forecastTotalCost: 0, forecastTotalBill: 0, cv: 0, actualHours: 0, ForecastHv: 0 };
          const businessDays = getBusinessDays(monthData.month, monthData.year);
          const hoursPerDay = 8;
          const isLinkedResource = !!lineData.linkedResource;
          // Parent lines are always read-only; sub-lines with linkedResource behave like old isResourceLine
          const lineIsReadOnly = mv.isLocked ||isReadOnly || (isParentLine && !isSubLine);

          return (
            <td key={`${monthData.year}-${monthData.month}`} className="month-column">
              <div className="month-column-content">
                <div className="in-out-inputs">
                  {forecastAllocationCol?.isVisible && <NumericFormat style={w("For. Allocation")} value={mv.forecastAllocation} onValueChange={(vals, sourceInfo) => {
                    if (lineIsReadOnly || sourceInfo?.source === 'prop') return;
                    const newAllocation = parseFloat(vals.value) || 0;
                    const derivedHours = parseFloat((newAllocation * hoursPerDay * businessDays).toFixed(2));
                    onForecastPairChange('forecastAllocation', 'forecastHours', lineData, monthData.month, monthData.year, newAllocation, derivedHours);
                  }} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} decimalScale={2} className={lineIsReadOnly ? "forecast-cell" : "forecast-input"} displayType={lineIsReadOnly ? "text" : undefined} />}
                  {forecastHoursCol?.isVisible && <NumericFormat style={w("For. Hours")} value={mv.forecastHours} onValueChange={(vals, sourceInfo) => {
                    if (lineIsReadOnly || sourceInfo?.source === 'prop') return;
                    const newHours = parseFloat(vals.value) || 0;
                    const derivedAllocation = parseFloat((newHours / (hoursPerDay * businessDays)).toFixed(4));
                    onForecastPairChange('forecastHours', 'forecastAllocation', lineData, monthData.month, monthData.year, newHours, derivedAllocation);
                  }} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} decimalScale={2} className={lineIsReadOnly ? "forecast-cell" : "forecast-input"} displayType={lineIsReadOnly ? "text" : undefined} />}
                  {actualHoursCol?.isVisible && (isLinkedResource || lineIsReadOnly
                    ? <NumericFormat style={w("Act. Hours")} value={mv.actualHours} displayType="text" thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} decimalScale={2} className="forecast-cell" />
                    : <NumericFormat style={w("Act. Hours")} value={mv.actualHours} onValueChange={(vals) => onActualHoursChange(lineData, monthData.month, monthData.year, parseFloat(vals.value) || 0)} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} decimalScale={2} className="forecast-input" />)}
                  {ForecastHvCol?.isVisible && <NumericFormat style={w("For. HV")} value={mv.ForecastHv} displayType="text" thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} decimalScale={2} className={`forecast-cell cv-cell ${(mv.ForecastHv < 0) && 'cv-slipping'}`} />}
                  {forecastHCostCol?.isVisible && <NumericFormat style={w("For. h. Cost")} value={mv.forecastHourlyCostRate} onValueChange={(vals) => { if (!lineIsReadOnly) onForecastChange(lineData, monthData.month, monthData.year, 'forecastHourlyCostRate', parseFloat(vals.value) || 0); }} prefix={settings.currencySymbol} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} decimalScale={2} className={lineIsReadOnly ? "forecast-cell" : "forecast-input"} displayType={lineIsReadOnly ? "text" : undefined} />}
                  {forecastHBillCol?.isVisible && <NumericFormat style={w("For. h. Bill")} value={mv.forecastHourlyBillRate} onValueChange={(vals) => { if (!lineIsReadOnly) onForecastChange(lineData, monthData.month, monthData.year, 'forecastHourlyBillRate', parseFloat(vals.value) || 0); }} prefix={settings.currencySymbol} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} decimalScale={2} className={lineIsReadOnly ? "forecast-cell" : "forecast-input"} displayType={lineIsReadOnly ? "text" : undefined} />}
                  {forecastTotalCostCol?.isVisible && <NumericFormat style={w("For. Total Cost")} value={mv.forecastTotalCost} displayType="text" prefix={settings.currencySymbol} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} decimalScale={2} className="forecast-cell" />}
                  {forecastTotalBillCol?.isVisible && <NumericFormat style={w("For. Total Bill")} value={mv.forecastTotalBill} displayType="text" prefix={settings.currencySymbol} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} decimalScale={2} className="forecast-cell" />}
                  {invCol?.isVisible && <NumericFormat style={w("INV")} value={mv.inv} displayType="text" thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} prefix={settings.currencySymbol} decimalScale={2} className="in-amount" />}
                  {acCol?.isVisible && (isLinkedResource || lineIsReadOnly
                    ? <NumericFormat style={w("AC")} value={mv.ac} displayType="text" thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} prefix={settings.currencySymbol} decimalScale={2} className="in-amount" />
                    : <NumericFormat style={w("AC")} value={mv.ac} onValueChange={(vals) => onOutAmountChange(lineData, monthData.month, monthData.year, parseFloat(vals.value) || 0)} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} prefix={settings.currencySymbol} decimalScale={2} className="out-amount" />)}
                  {forecastCvCol?.isVisible && <NumericFormat style={w("For. CV")} value={mv.cv} displayType="text" prefix={settings.currencySymbol} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} decimalScale={2} className={`forecast-cell cv-cell ${(mv.cv < 0) && 'cv-slipping'}`} />}
                  {forecastGpCol?.isVisible && <input style={w("For. GP")} type="text" className="gp-percentage" disabled value={formatPercentage(mv.forecastTotalBill > 0 ? ((mv.forecastTotalBill - mv.forecastTotalCost) / mv.forecastTotalBill) * 100 : 0)} />}
                  {gpCol?.isVisible && <input style={w("GP")} type="text" className="gp-percentage" disabled value={formatPercentage(mv.gpPercentage)} />}
                </div>
              </div>
            </td>
          );
        };

        return (
          <>
            {/* Main line row (standalone or parent) */}
            <tr key={line.id} className={`detail-row ${isParentLine ? 'parent-line-row' : ''}`}>
              <td className="category-column">
                <div className="remove-line-container">
                  {!isReadOnly && !isParentLine && !line.linkedResource && (canUpdateLine === null || canUpdateLine === true) && (
                    <button className="remove-line-button" onClick={() => handleRemoveRow(line.id)} title="Remove line">
                      <X size={14} />
                    </button>
                  )}
                </div>
              </td>
              {descriptionCol?.isVisible !== false && (
                <td className="description-column">
                  {isParentLine ? (
                    <button className="parent-expand-button" onClick={() => toggleParent(line.id)}>
                      <ChevronRight size={14} className={`transform ${expandedParents.has(line.id) ? 'rotate-90' : ''}`} />
                      <span className="parent-line-description">{line.description}</span>
                    </button>
                  ) : (
                    <input type="text" className="description"
                      value={descriptions[line.id] || ''}
                      onChange={(e) => !isReadOnly && checkCanUpdateDesc(line, e.target.value)}
                      onFocus={() => !isReadOnly && descriptionHandler(line)}
                      placeholder="Description"
                      disabled={isReadOnly || !!line.linkedResource || canUpdateLine === false}
                      style={{
                        backgroundColor: (isReadOnly || canUpdateLine === false) ? "#EDECF8" : "",
                        cursor: updateCheckLoading ? 'progress' : ''
                      }}
                    />
                  )}
                </td>
              )}
              {cashFlowData.map(monthData => renderLineCells(line, monthData, false))}
              {totalsCol?.isVisible !== false && (
                <td className="totals-column">
                  <div className="month-column-content">
                    {renderTotalsCells(lineForecast, lineInv, lineAc, lineGpPct)}
                  </div>
                </td>
              )}
            </tr>

            {/* Sub-line rows rendered under their parent, only when expanded */}
            {isParentLine && expandedParents.has(line.id) && line.children.map(subLine => {
              const subLineForecast = calculateLineForecastTotals(subLine.months);
              const subLineInv = Array.from(subLine.months.values()).reduce((s, m) => s + (m.inv || 0), 0);
              const subLineAc = Array.from(subLine.months.values()).reduce((s, m) => s + (m.ac || 0), 0);
              const subLineGpPct = subLineInv > 0 ? ((subLineInv - subLineAc) / subLineInv) * 100 : 0;
              const resource = subLine.linkedResource;
              return (
                <tr key={subLine.id} className="detail-row sub-line-row">
                  <td className="category-column">
                    <div className="remove-line-container">
                      {/* Sub-lines with linkedResource are not removable */}
                    </div>
                  </td>
                  {descriptionCol?.isVisible !== false && (
                    <td className="description-column">
                      <div className="sub-line-resource-cell">
                        {resource ? (
                          <HoverCard
                            AvatarId={resource.id}
                            AvatarName={resource.name}
                            AvatarImage={resource.image}
                            AvatarDesignation={resource.title}
                            AvatarEmail={resource.email}
                            AvatarPhone={resource.phoneNumber}
                            AvatarIsExternal={resource.isExternal}
                            CanSendMessage={true}
                            HoverCardTrigger={
                              <>
                                <Avatar
                                  size={20}
                                  name={resource.name}
                                  image={{ src: resource.image }}
                                  color="colorful"
                                />
                                <span className="sub-line-resource-name">{resource.name}</span>
                              </>

                            }
                          />
                        ) : (
                          <span className="sub-line-resource-name">{subLine.description}</span>
                        )}
                      </div>
                    </td>
                  )}
                  {cashFlowData.map(monthData => renderLineCells(subLine, monthData, true))}
                  {totalsCol?.isVisible !== false && (
                    <td className="totals-column">
                      <div className="month-column-content">
                        {renderTotalsCells(subLineForecast, subLineInv, subLineAc, subLineGpPct)}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </>
        );
      })}
    </>
  );
};

export default CashFlowRow;